'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { BasicClose, BasicDuplicate, BasicEyeClosed, BasicEyeOpen } from '../../icons/basic';
import { IconButton } from '../buttons/IconButton';
import { Typography } from '../typography/Typography';
import { InteractiveJSON } from './InteractiveJSON';

// Safe environment variable access to prevent build issues in different bundlers
const safeGetEnv = (key: string): string | undefined => {
  try {
    // Use bracket notation to prevent bundlers from trying to replace these at build time
    return typeof process !== 'undefined' && process.env ? process.env[key] : undefined;
  } catch {
    return undefined;
  }
};

interface NetworkConnection {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
}

interface ExtendedNavigator extends Navigator {
  connection?: NetworkConnection;
  mozConnection?: NetworkConnection;
  webkitConnection?: NetworkConnection;
}

interface DebugPanelProps {
  /**
   * Environment name to display on the button
   */
  readonly environmentName?: string;
  /**
   * Custom environment variables to display
   * If not provided, will attempt to read from window object
   */
  readonly environmentVariables?: Record<string, string>;
  /**
   * Additional information to display in the "Other Info" section
   */
  readonly otherInfo?: Record<string, unknown>;
  /**
   * Search parameters from the current page (for URL debugging)
   */
  readonly searchParams?: { [key: string]: string | string[] | undefined };
  /**
   * Current path parts for debugging
   */
  readonly currentPathParts?: string[];
  /**
   * Whether to hide in production (default: true)
   */
  readonly hideInProduction?: boolean;
}

/**
 * DebugPanel component - A comprehensive development and debugging tool
 *
 * Features:
 * - Environment detection and display
 * - Build information (BUILD_ID and COMMIT_ID) and version details
 * - Interactive environment variables viewer
 * - Page data debugging with JSON explorer
 * - Session management controls
 * - Production safety (auto-hides in production)
 *
 * Production Detection:
 * - Checks ENVIRONMENT variable for 'production' value first
 * - Falls back to hostname-based detection for known production domains
 * - Supports multiple environment variable formats (ENVIRONMENT, NEXT_PUBLIC_ENVIRONMENT, etc.)
 * - Explicitly hides on all known production domains including mijn.* and www-prod.* subdomains
 *
 * Shows a button at bottom-right that opens a full-featured debugging overlay
 * Displays both BUILD_ID and COMMIT_ID when available from environment variables
 */
export function DebugPanel({
  environmentName,
  environmentVariables,
  otherInfo,
  searchParams,
  currentPathParts,
  hideInProduction = true,
}: DebugPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isHiddenForSession, setIsHiddenForSession] = useState(false);
  const [envVars, setEnvVars] = useState<Record<string, string>>({});
  const [currentEnv, setCurrentEnv] = useState<string>('');
  const [buildId, setBuildId] = useState<string>('');
  const [commitId, setCommitId] = useState<string>('');
  const [clientInfo, setClientInfo] = useState<Record<string, string>>({});
  const [copyFeedback, setCopyFeedback] = useState<string>('');

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    pageInfo: true, // Keep page info expanded by default
    networkInfo: false,
    pageData: true, // Keep page data expanded by default
  });

  // Convert searchParams to query string for display
  const queryString = useMemo(() => {
    if (!searchParams || Object.keys(searchParams).length === 0) {
      return '';
    }

    const urlSearchParams = new URLSearchParams();
    Object.entries(searchParams).forEach(([key, value]) => {
      if (value !== undefined) {
        if (Array.isArray(value)) {
          value.forEach((v) => {
            try {
              urlSearchParams.append(decodeURIComponent(key), decodeURIComponent(v));
            } catch {
              urlSearchParams.append(key, v);
            }
          });
        } else {
          try {
            urlSearchParams.set(decodeURIComponent(key), decodeURIComponent(String(value)));
          } catch {
            urlSearchParams.set(key, String(value));
          }
        }
      }
    });

    return urlSearchParams.toString() ? `?${urlSearchParams.toString()}` : '';
  }, [searchParams]);

  // Generate current path for display
  const currentPath = useMemo(() => {
    return currentPathParts && currentPathParts.length > 0
      ? `/${currentPathParts.join('/')}`
      : '/';
  }, [currentPathParts]);

  // Helper functions to collect client-side information
  const collectNavigatorInfo = (): Record<string, string> => {
    const data: Record<string, string> = {};
    if (typeof navigator === 'undefined') {
      return data;
    }

    data['User Agent'] = navigator.userAgent;
    data['Locale'] = navigator.language;
    data['Platform'] = navigator.platform;
    return data;
  };

  const collectViewportInfo = (): Record<string, string> => {
    const data: Record<string, string> = {};
    if (typeof window === 'undefined') {
      return data;
    }

    data['Screen & Viewport'] = `${window.screen.width}x${window.screen.height} (${window.innerWidth}x${window.innerHeight}px, DPR: ${window.devicePixelRatio || 1})`;
    data['Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return data;
  };

  const collectConnectionInfo = (): Record<string, string> => {
    const data: Record<string, string> = {};
    if (typeof window === 'undefined') {
      return data;
    }

    const extendedNavigator = navigator as ExtendedNavigator;
    const connection = extendedNavigator.connection || extendedNavigator.mozConnection || extendedNavigator.webkitConnection;

    if (connection) {
      const effectiveType = connection.effectiveType || 'unknown';
      const downlink = connection.downlink;
      const rtt = connection.rtt;

      if (downlink) {
        data['Connection Speed'] = `${downlink} Mbps (${effectiveType})`;
      } else {
        data['Connection Quality'] = effectiveType;
      }

      if (rtt) {
        data['Network Latency'] = `${rtt}ms`;
      }
    } else {
      data['Connection Info'] = 'Not available (likely desktop)';
    }

    data['Online Status'] = navigator.onLine ? 'Online' : 'Offline';

    if ('hardwareConcurrency' in navigator) {
      data['CPU Cores'] = navigator.hardwareConcurrency.toString();
    }

    return data;
  };

  // Collect client-side information
  useEffect(() => {
    const clientData: Record<string, string> = {
      ...collectNavigatorInfo(),
      ...collectViewportInfo(),
      ...collectConnectionInfo(),
    };

    setClientInfo(clientData);
  }, []);

  // Helper function to check if we're in production
  const checkIfProduction = useCallback(() => {
    if (!hideInProduction) return false;

    // First check: If ENVIRONMENT variable is explicitly set to 'production', hide the debug panel
    const environmentVar = safeGetEnv('ENVIRONMENT') ||
      safeGetEnv('NEXT_PUBLIC_ENVIRONMENT') ||
      safeGetEnv('VITE_ENVIRONMENT') ||
      safeGetEnv('REACT_APP_ENVIRONMENT') ||
      (environmentVariables && (
        environmentVariables.ENVIRONMENT ||
        environmentVariables.NEXT_PUBLIC_ENVIRONMENT ||
        environmentVariables.VITE_ENVIRONMENT ||
        environmentVariables.REACT_APP_ENVIRONMENT
      ));

    if (environmentVar?.toUpperCase() === 'PRODUCTION') {
      return true;
    }

    // Second check: hostname-based detection for actual production domains
    const hostname = window.location.hostname;

    // List of all known production domains where debug panel should be hidden
    const productionDomains = [
      // Woonstad production domains
      'www.woonstadrotterdam.nl',
      'woonstadrotterdam.nl',
      'mijn.woonstadrotterdam.nl',
      // Stadswonen production domains
      'www.stadswonenrotterdam.nl',
      'stadswonenrotterdam.nl',
      'mijn.stadswonenrotterdam.nl',
    ];

    // Check if current hostname is a known production domain
    const isKnownProductionDomain = productionDomains.includes(hostname);

    // General production detection for other domains
    const isGeneralProduction =
      (hostname.includes('.nl') || hostname.includes('.com') || hostname.includes('.org')) &&
      !hostname.includes('tst') &&
      !hostname.includes('test') &&
      !hostname.includes('acc') &&
      !hostname.includes('staging') &&
      !hostname.includes('dev') &&
      !hostname.includes('app') &&
      !hostname.includes('localhost') &&
      !hostname.includes('127.0.0.1') &&
      !hostname.includes('0.0.0.0') &&
      // Exclude known non-production domains for Woonstad/Stadswonen projects
      !hostname.includes('woonstadrotterdam') &&
      !hostname.includes('stadswonenrotterdam');

    const isActualProduction = isKnownProductionDomain || isGeneralProduction;

    return isActualProduction;
  }, [hideInProduction, environmentVariables]);

  // Helper function to detect environment
  const detectEnvironment = useCallback(() => {
    if (environmentName) {
      return environmentName;
    }

    const hostname = window.location.hostname;

    // For localhost, always check API URL for arrow display, regardless of environment variables
    if (hostname.includes('localhost') || hostname.includes('127.0.0.1') || hostname.includes('0.0.0.0')) {
      const apiUrl = safeGetEnv('CONTENT_DELIVERY_API_BASE_URL') ||
        safeGetEnv('NEXT_PUBLIC_CONTENT_DELIVERY_API_BASE_URL') ||
        safeGetEnv('GATEWAY_BASE_URL') ||
        safeGetEnv('VITE_GATEWAY_BASE_URL') ||
        safeGetEnv('OKAPI_BASE_URL') ||
        safeGetEnv('VITE_OKAPI_BASE_URL') ||
        safeGetEnv('REACT_APP_API_BASE_URL') ||
        safeGetEnv('VITE_API_BASE_URL') ||
        safeGetEnv('API_BASE_URL') ||
        (environmentVariables && (
          environmentVariables.CONTENT_DELIVERY_API_BASE_URL ||
          environmentVariables.NEXT_PUBLIC_CONTENT_DELIVERY_API_BASE_URL ||
          environmentVariables.GATEWAY_BASE_URL ||
          environmentVariables.VITE_GATEWAY_BASE_URL ||
          environmentVariables.OKAPI_BASE_URL ||
          environmentVariables.VITE_OKAPI_BASE_URL ||
          environmentVariables.REACT_APP_API_BASE_URL ||
          environmentVariables.VITE_API_BASE_URL ||
          environmentVariables.API_BASE_URL
        )) ||
        '';

      if (apiUrl.includes('test')) return 'LOCAL → TEST';
      if (apiUrl.includes('acc')) return 'LOCAL → ACC';
      if (apiUrl.includes('prod')) return 'LOCAL → PROD';

      return 'LOCAL';
    }

    // Check ENVIRONMENT variable for non-localhost environments
    const environmentVar = safeGetEnv('ENVIRONMENT') ||
      safeGetEnv('NEXT_PUBLIC_ENVIRONMENT') ||
      safeGetEnv('VITE_ENVIRONMENT') ||
      safeGetEnv('REACT_APP_ENVIRONMENT') ||
      (environmentVariables && (
        environmentVariables.ENVIRONMENT ||
        environmentVariables.NEXT_PUBLIC_ENVIRONMENT ||
        environmentVariables.VITE_ENVIRONMENT ||
        environmentVariables.REACT_APP_ENVIRONMENT
      ));

    if (environmentVar) {
      return environmentVar.toUpperCase();
    }

    if (hostname.includes('tst')) return 'TEST';
    if (hostname.includes('acc')) return 'ACCEPTANCE';
    if (hostname.includes('app')) return 'BETA';

    return 'UNKNOWN';
  }, [environmentName, environmentVariables]);

  // Helper function to detect build ID
  const detectBuildId = useCallback(() => {
    const buildIdFromEnv = safeGetEnv('NEXT_PUBLIC_BUILD_ID') ||
      safeGetEnv('REACT_APP_BUILD_ID') ||
      safeGetEnv('VITE_BUILD_ID') ||
      safeGetEnv('BUILD_ID') ||
      (environmentVariables && (
        environmentVariables.NEXT_PUBLIC_BUILD_ID ||
        environmentVariables.REACT_APP_BUILD_ID ||
        environmentVariables.VITE_BUILD_ID ||
        environmentVariables.BUILD_ID
      ));

    if (buildIdFromEnv) return buildIdFromEnv;

    const isDevelopment = safeGetEnv('NODE_ENV') === 'development' ||
      safeGetEnv('REACT_APP_NODE_ENV') === 'development' ||
      safeGetEnv('VITE_NODE_ENV') === 'development' ||
      window.location.hostname.includes('localhost') ||
      window.location.hostname.includes('127.0.0.1') ||
      window.location.hostname.includes('0.0.0.0');

    return isDevelopment ? 'dev-local-build' : 'BUILD_ID missing';
  }, [environmentVariables]);

  // Helper function to detect commit ID
  const detectCommitId = useCallback(() => {
    const commitIdFromEnv = safeGetEnv('NEXT_PUBLIC_COMMIT_ID') ||
      safeGetEnv('REACT_APP_COMMIT_ID') ||
      safeGetEnv('VITE_COMMIT_ID') ||
      safeGetEnv('COMMIT_ID') ||
      safeGetEnv('NEXT_PUBLIC_GIT_COMMIT') ||
      safeGetEnv('REACT_APP_GIT_COMMIT') ||
      safeGetEnv('VITE_GIT_COMMIT') ||
      safeGetEnv('GIT_COMMIT') ||
      safeGetEnv('NEXT_PUBLIC_GIT_SHA') ||
      safeGetEnv('REACT_APP_GIT_SHA') ||
      safeGetEnv('VITE_GIT_SHA') ||
      safeGetEnv('GIT_SHA') ||
      (environmentVariables && (
        environmentVariables.NEXT_PUBLIC_COMMIT_ID ||
        environmentVariables.REACT_APP_COMMIT_ID ||
        environmentVariables.VITE_COMMIT_ID ||
        environmentVariables.COMMIT_ID ||
        environmentVariables.NEXT_PUBLIC_GIT_COMMIT ||
        environmentVariables.REACT_APP_GIT_COMMIT ||
        environmentVariables.VITE_GIT_COMMIT ||
        environmentVariables.GIT_COMMIT ||
        environmentVariables.NEXT_PUBLIC_GIT_SHA ||
        environmentVariables.REACT_APP_GIT_SHA ||
        environmentVariables.VITE_GIT_SHA ||
        environmentVariables.GIT_SHA
      ));

    if (commitIdFromEnv) {
      // If commit ID is a full SHA, show only the first 7 characters for display
      return commitIdFromEnv.length > 7 ? commitIdFromEnv.slice(0, 7) : commitIdFromEnv;
    }

    const isDevelopment = safeGetEnv('NODE_ENV') === 'development' ||
      safeGetEnv('REACT_APP_NODE_ENV') === 'development' ||
      safeGetEnv('VITE_NODE_ENV') === 'development' ||
      window.location.hostname.includes('localhost') ||
      window.location.hostname.includes('127.0.0.1') ||
      window.location.hostname.includes('0.0.0.0');

    return isDevelopment ? 'dev-local' : 'COMMIT_ID missing';
  }, [environmentVariables]);

  // Helper function to collect environment variables
  const collectEnvironmentVariables = useCallback(() => {
    const vars: Record<string, string> = {};

    if (environmentVariables) {
      Object.assign(vars, environmentVariables);
    }

    // Collect from process.env (build-time variables) safely
    try {
      if (typeof process !== 'undefined' && process.env) {
        Object.entries(process.env).forEach(([key, value]) => {
          if (value && typeof value === 'string') {
            vars[key] = value;
          }
        });
      }
    } catch {
      // Ignore errors in environments where process is not available
    }

    // Collect from window object (runtime variables)
    if (typeof window !== 'undefined') {
      Object.entries(window).forEach(([key, value]) => {
        if (
          typeof value === 'string' &&
          value && // Not empty
          (key.includes('CLIENT_ID') ||
            key.includes('TENANT_ID') ||
            key.includes('AUDIENCE') ||
            key.includes('_BASE_URL') ||
            key.includes('_API_') ||
            key.includes('RELEASE') ||
            key.includes('BUILD_ID') ||
            key.includes('COMMIT_ID') ||
            key.includes('GIT_COMMIT') ||
            key.includes('GIT_SHA') ||
            key.startsWith('REACT_APP_') ||
            key.startsWith('NEXT_PUBLIC_') ||
            key.startsWith('VITE_') ||
            key.includes('_URL') ||
            key.includes('_KEY') ||
            key.includes('_TOKEN') ||
            key.includes('_ENV') ||
            key.toUpperCase() === key) // All caps likely env var
        ) {
          if (!vars[key]) {
            vars[key] = value;
          }
        }
      });
    }

    return vars;
  }, [environmentVariables]);

  // Helper function to format value with URL decoding
  const formatValue = useCallback((key: string, value: unknown): string => {
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value, null, 2);
    }

    const stringValue = String(value);
    const urlRelatedKeys = ['Query String', 'Current Path', 'Request URL', 'Route', 'Path', 'URL', 'Pathname'];

    const shouldDecode = urlRelatedKeys.some((urlKey) =>
      key.toLowerCase().includes(urlKey.toLowerCase()));

    if (shouldDecode && (stringValue.includes('%') || stringValue.includes('+'))) {
      try {
        const decoded = decodeURIComponent(stringValue);

        return decoded !== stringValue ? decoded : stringValue;
      } catch {
        return stringValue;
      }
    }

    return stringValue;
  }, []);

  useEffect(() => {
    // Check if we're in production and should hide
    if (checkIfProduction()) {
      return;
    }

    // Check sessionStorage for hide preference
    const shouldHide = sessionStorage.getItem('hideDebugPanel') === 'true';
    setIsHiddenForSession(shouldHide);

    if (shouldHide) {
      setIsVisible(false);

      return;
    }

    // Set environment, build ID, commit ID and environment variables
    setCurrentEnv(detectEnvironment());
    setBuildId(detectBuildId());
    setCommitId(detectCommitId());
    setEnvVars(collectEnvironmentVariables());
  }, [checkIfProduction, detectEnvironment, detectBuildId, detectCommitId, collectEnvironmentVariables]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save current overflow style
      const originalOverflow = document.body.style.overflow;

      // Lock scroll
      document.body.style.overflow = 'hidden';

      // Cleanup function to restore scroll
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleToggleSessionVisibility = () => {
    const newHiddenState = !isHiddenForSession;
    setIsHiddenForSession(newHiddenState);

    if (newHiddenState) {
      sessionStorage.setItem('hideDebugPanel', 'true');
    } else {
      sessionStorage.removeItem('hideDebugPanel');
    }
  };

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const handleCopyPageData = useCallback(async () => {
    if (!otherInfo?.['__PAGE_DATA__']) {
      return;
    }

    try {
      // Use JSON.stringify to ensure proper escaping of all control characters
      const pageDataString = JSON.stringify(otherInfo['__PAGE_DATA__'], null, 2);
      await navigator.clipboard.writeText(pageDataString);
      setCopyFeedback('Copied!');

      // Clear feedback after 2 seconds
      setTimeout(() => {
        setCopyFeedback('');
      }, 2000);
    } catch (error) {
      setCopyFeedback('Failed to copy');
      setTimeout(() => {
        setCopyFeedback('');
      }, 2000);
    }
  }, [otherInfo]);

  // Handle ESC key to close overlay
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, handleClose]);

  // Don't render if no environment detected (likely production)
  if (!currentEnv) {
    return null;
  }

  return (
    <>
      {/* Environment Button */}
      {!isOpen && isVisible && (
        <div className="fixed bottom-4 right-4 z-50">
          <div className="relative">
            <button
              onClick={handleToggle}
              className="hover:shadow-medium focus:ring-button-primary-enabled rounded-2xl bg-button-primary-enabled px-4 py-3 text-button-primary-enabled shadow-small transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2"
              aria-label={`Show environment information for ${currentEnv}`}
            >
              <div className="text-center">
                <Typography
                  variant="content-body-bold"
                  type="p"
                  className=""
                >
                  {currentEnv}
                </Typography>
              </div>
            </button>
            <button
              onClick={() => setIsVisible(false)}
              className="hover:bg-surface-secondary-on-light absolute -right-2 -top-2 flex size-6 items-center justify-center rounded-full bg-surface-default shadow-small transition-colors"
              aria-label="Dismiss debug panel"
            >
              <BasicClose className="size-3 text-base-primary-on-light" />
            </button>
          </div>
        </div>
      )}

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 cursor-default backdrop-blur-sm"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }}
        >
          {/* Modal Content */}
          <dialog
            open
            className="fixed inset-0 z-50 m-0 flex h-screen max-h-screen w-screen cursor-default flex-col rounded-none border-0 bg-surface-default p-0 shadow-small md:inset-4 md:h-[calc(100vh-2rem)] md:max-h-[calc(100vh-2rem)] md:w-[calc(100vw-2rem)] md:rounded-lg"
            aria-labelledby="debug-panel-title"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-[#c1ccd7] px-4 py-2">
              <Typography
                variant="content-body"
                type="h2"
                className="text-base-primary-on-light"
                id="debug-panel-title"
              >
                Debug Panel
              </Typography>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleToggleSessionVisibility}
                  className="hover:bg-base-tertiary-on-light text-xs flex items-center gap-2 rounded px-2 py-1 text-base-secondary-on-light transition-colors hover:text-base-primary-on-light"
                  title={isHiddenForSession ? 'Show debug panel button for this session' : 'Hide debug panel button for this session'}
                >
                  {isHiddenForSession ? (
                    <>
                      <BasicEyeOpen className="size-5" />
                      Unhide for session
                    </>
                  ) : (
                    <>
                      <BasicEyeClosed className="size-5" />
                      Hide for session
                    </>
                  )}
                </button>
                <IconButton
                  icon={<BasicClose />}
                  onClick={handleClose}
                  label="Close debug panel"
                  className="text-base-secondary-on-light hover:text-base-primary-on-light"
                />
              </div>
            </div>

            {/* Environment Name */}
            <div className="bg-base-tertiary-on-light px-4 py-2">
              <div className="flex items-center justify-between">
                <div>
                  <Typography
                    variant="label-small"
                    type="span"
                    className="text-base-secondary-on-light"
                  >
                    Current Environment
                  </Typography>
                  <Typography
                    variant="content-body"
                    type="p"
                    className="text-base-primary-on-light"
                  >
                    {currentEnv}
                  </Typography>
                </div>
                <div className="flex gap-6">
                  <div className="text-right">
                    <Typography
                      variant="label-small"
                      type="span"
                      className="text-base-secondary-on-light"
                    >
                      Build ID
                    </Typography>
                    <Typography
                      variant="content-body"
                      type="p"
                      className="font-mono text-base-primary-on-light"
                    >
                      {buildId}
                    </Typography>
                  </div>
                  <div className="text-right">
                    <Typography
                      variant="label-small"
                      type="span"
                      className="text-base-secondary-on-light"
                    >
                      Commit ID
                    </Typography>
                    <Typography
                      variant="content-body"
                      type="p"
                      className="font-mono text-base-primary-on-light"
                    >
                      {commitId}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>

            {/* Section Titles - Hidden on mobile */}
            <div className="hidden border-b border-[#c1ccd7] px-4 py-2 md:grid md:grid-cols-2 md:gap-4">
              <Typography
                variant="content-body"
                type="h3"
                className="text-base-primary-on-light"
              >
                Environment Variables
              </Typography>
              <Typography
                variant="content-body"
                type="h3"
                className="text-base-primary-on-light md:pl-4"
              >
                Debug Information
              </Typography>
            </div>

            {/* Content Areas */}
            <div className="flex flex-1 flex-col overflow-hidden md:grid md:grid-cols-2 md:gap-4">
              {/* Environment Variables */}
              <div className="flex h-full min-h-0 flex-col border-y border-[#c1ccd7] md:border-y-0">
                <div className="flex-1 overflow-y-auto p-4">
                  {Object.keys(envVars).length === 0 ? (
                    <Typography
                      variant="label-small"
                      type="p"
                      className="text-base-secondary-on-light"
                    >
                      No environment variables found
                    </Typography>
                  ) : (
                    <div className="space-y-3">
                      {(() => {
                        const sortedEnvVars = Object.entries(envVars).toSorted(([a], [b]) => a.localeCompare(b));

                        return sortedEnvVars.map(([key, value], index) => (
                          <div
                            key={key}
                            className={`${index === sortedEnvVars.length - 1 ? '' : 'border-b border-[#c1ccd7] pb-3'}`}
                          >
                            <Typography
                              variant="label-small"
                              type="p"
                              className="font-mono text-base-primary-on-light"
                            >
                              {key}
                            </Typography>
                            <Typography
                              variant="label-small"
                              type="p"
                              className="font-mono mt-1 whitespace-pre-wrap break-all text-base-secondary-on-light"
                            >
                              {value}
                            </Typography>
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* Other Info */}
              <div className="flex h-full min-h-0 flex-col border-t border-[#c1ccd7] md:border-l md:border-t-0">
                <div className="flex-1 overflow-y-auto p-4">
                  {!otherInfo || Object.keys(otherInfo).length === 0 ? (
                    <Typography
                      variant="label-small"
                      type="p"
                      className="text-base-secondary-on-light"
                    >
                      No other information provided
                    </Typography>
                  ) : (
                    <>
                      {/* Regular other info (excluding __PAGE_DATA__) */}
                      {(() => {
                        const regularEntries = Object.entries(otherInfo).filter(([key]) => key !== '__PAGE_DATA__');
                        const pageData = otherInfo['__PAGE_DATA__'];

                        // Separate client info into categories
                        const clientEntries = Object.entries(clientInfo);

                        const systemInfo: [string, unknown][] = clientEntries.filter(([key]) =>
                          ([] as string[]).includes(key));

                        const networkInfo = clientEntries.filter(([key]) =>
                          ['User Agent', 'Locale', 'Platform', 'Screen & Viewport', 'Timezone', 'Connection Speed', 'Connection Quality', 'Connection Info', 'Network Latency', 'Online Status', 'CPU Cores'].includes(key));

                        // Add computed URL info if searchParams/currentPathParts were provided
                        const urlInfo: [string, unknown][] = [];

                        if (currentPathParts !== undefined) {
                          urlInfo.push(['Current Path', currentPath]);
                        }

                        if (searchParams !== undefined) {
                          urlInfo.push(['Query String', queryString || '(none)']);
                        }

                        const allEntries = [...regularEntries, ...systemInfo, ...urlInfo];

                        return (
                          <>
                            {allEntries.length > 0 && (
                              <div className="">
                                <button
                                  onClick={() => toggleSection('pageInfo')}
                                  className="hover:bg-surface-secondary-on-light flex w-full items-center justify-between rounded py-3 pr-1 text-left"
                                >
                                  <Typography
                                    variant="content-body"
                                    type="h4"
                                    className="text-base-primary-on-light"
                                  >
                                    Page Info
                                  </Typography>
                                  <span className="text-2xl font-bold text-base-secondary-on-light">
                                    {expandedSections.pageInfo ? '−' : '+'}
                                  </span>
                                </button>
                                {expandedSections.pageInfo && (
                                  <div className="space-y-3">
                                    {(() => {
                                      const sortedAllEntries = allEntries.toSorted(([a], [b]) => a.localeCompare(b));

                                      return sortedAllEntries.map(([key, value], index) => (
                                        <div
                                          key={key}
                                          className={`${index === sortedAllEntries.length - 1 ? '' : 'border-b border-[#c1ccd7] pb-3'}`}
                                        >
                                          <Typography
                                            variant="label-small"
                                            type="p"
                                            className="font-mono text-base-primary-on-light"
                                          >
                                            {key}
                                          </Typography>
                                          <Typography
                                            variant="label-small"
                                            type="p"
                                            className="font-mono mt-1 whitespace-pre-wrap break-all text-base-secondary-on-light"
                                          >
                                            {formatValue(key, value)}
                                          </Typography>
                                        </div>
                                      ));
                                    })()}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Network & Device Info section */}
                            {networkInfo.length > 0 && (
                              <div className="border-t border-[#c1ccd7]">
                                <button
                                  onClick={() => toggleSection('networkInfo')}
                                  className="hover:bg-surface-secondary-on-light flex w-full items-center justify-between rounded py-3 pr-1 text-left"
                                >
                                  <Typography
                                    variant="content-body"
                                    type="h4"
                                    className="text-base-primary-on-light"
                                  >
                                    Network & Device Info
                                  </Typography>
                                  <span className="text-2xl font-bold text-base-secondary-on-light">
                                    {expandedSections.networkInfo ? '−' : '+'}
                                  </span>
                                </button>
                                {expandedSections.networkInfo && (
                                  <div className="space-y-3">
                                    {(() => {
                                      const sortedNetworkInfo = networkInfo.toSorted(([a], [b]) => a.localeCompare(b));

                                      return sortedNetworkInfo.map(([key, value], index) => (
                                        <div
                                          key={key}
                                          className={`${index === sortedNetworkInfo.length - 1 ? '' : 'border-b border-[#c1ccd7] pb-3'}`}
                                        >
                                          <Typography
                                            variant="label-small"
                                            type="p"
                                            className="font-mono text-base-primary-on-light"
                                          >
                                            {key}
                                          </Typography>
                                          <Typography
                                            variant="label-small"
                                            type="p"
                                            className="font-mono mt-1 whitespace-pre-wrap break-all text-base-secondary-on-light"
                                          >
                                            {formatValue(key, value)}
                                          </Typography>
                                        </div>
                                      ));
                                    })()}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Page Data section at the bottom */}
                            {pageData && (
                              <div className="border-t border-[#c1ccd7]">
                                <div className="flex items-center justify-between py-3 pr-1">
                                  <button
                                    onClick={() => toggleSection('pageData')}
                                    className="hover:bg-surface-secondary-on-light flex items-center gap-2 rounded py-3 text-left"
                                  >
                                    <Typography
                                      variant="content-body"
                                      type="h4"
                                      className="text-base-primary-on-light"
                                    >
                                      Page Data
                                    </Typography>
                                    <span className="text-2xl font-bold text-base-secondary-on-light">
                                      {expandedSections.pageData ? '−' : '+'}
                                    </span>
                                  </button>
                                  <button
                                    onClick={handleCopyPageData}
                                    className="hover:bg-surface-secondary-on-light flex items-center gap-1 rounded px-2 py-1 text-xs text-base-secondary-on-light transition-colors hover:text-base-primary-on-light"
                                    title="Copy page data to clipboard"
                                  >
                                    <BasicDuplicate className="size-4" />
                                    {copyFeedback || 'Copy'}
                                  </button>
                                </div>
                                {expandedSections.pageData && (
                                  <div
                                    style={{
                                      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, "Courier New", Courier, monospace',
                                    }}
                                  >
                                    <InteractiveJSON data={pageData} />
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </>
                  )}
                </div>
              </div>
            </div>
          </dialog>
        </div>
      )}
    </>
  );
}
